/**
 * VideoStudioPage — the in-platform video edit-loop (P4).
 *
 * The video is a VideoSpec (data), so iterating is: pick a spec → edit it → render
 * a preview → watch → repeat, all in the browser, no code. This is the functional
 * first version: a spec picker, a JSON editor (validated on save), a render button,
 * and the player. A scene-strip UI + conversational spec-patch are the next polish.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type SpecSummary = { name: string; type: string; title: string; scenes: number; invalid?: boolean };

const c = {
  bg: '#1a1a1e', panel: '#232329', ink: '#ECEAE4', mut: '#9A968F', line: '#34343c',
  accent: '#44BEE8', good: '#4CAF6E', bad: '#E1544A', mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

export default function VideoStudioPage() {
  const [specs, setSpecs] = useState<SpecSummary[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [json, setJson] = useState<string>('');
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState<{ kind: 'idle' | 'ok' | 'err' | 'busy'; msg: string }>({ kind: 'idle', msg: '' });
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [scale, setScale] = useState<1 | 2>(1);
  const [regenAudio, setRegenAudio] = useState(false);
  const [master, setMaster] = useState(false); // default = fast draft
  const [previewScene, setPreviewScene] = useState(''); // '' = whole video
  const [log, setLog] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [authoring, setAuthoring] = useState(false);
  const [brief, setBrief] = useState({ topic: '', audience: '', type: 'explainer', targetSec: 60, name: '' });

  // Scene ids parsed from the current spec — drives the per-scene preview picker
  // (render just one scene → the fastest edit-loop).
  const sceneIds: string[] = (() => {
    try {
      const s = JSON.parse(json);
      return Array.isArray(s.scenes) ? s.scenes.map((sc: { id?: string }) => sc?.id).filter(Boolean) : [];
    } catch { return []; }
  })();

  const loadSpecs = async () => {
    const r = await fetch('/api/video/specs').then((x) => x.json());
    setSpecs(r.specs || []);
    if (!selected && r.specs?.[0]) selectSpec(r.specs[0].name);
  };
  useEffect(() => { loadSpecs(); /* eslint-disable-line */ }, []);

  const selectSpec = async (name: string) => {
    setSelected(name);
    setVideoUrl(''); setLog(''); setStatus({ kind: 'idle', msg: '' });
    const txt = await fetch(`/api/video/spec/${name}`).then((x) => x.text());
    try { setJson(JSON.stringify(JSON.parse(txt), null, 2)); } catch { setJson(txt); }
    setDirty(false);
  };

  const author = async () => {
    if (!brief.topic.trim()) { setStatus({ kind: 'err', msg: 'describe the video first' }); return; }
    setAuthoring(true);
    setStatus({ kind: 'busy', msg: 'authoring a spec from your brief… (grounded in the Moxo brain)' });
    try {
      const r = await fetch('/api/video/author', {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(brief),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setStatus({ kind: 'ok', msg: `created “${data.name}” — ${(data.order || []).join(' ')}` });
        setShowNew(false); setBrief({ topic: '', audience: '', type: 'explainer', targetSec: 60, name: '' });
        await loadSpecs(); selectSpec(data.name);
      } else setStatus({ kind: 'err', msg: data.detail || data.error || 'author failed' });
    } catch (e) { setStatus({ kind: 'err', msg: String((e as Error).message) }); }
    finally { setAuthoring(false); }
  };

  const save = async () => {
    setStatus({ kind: 'busy', msg: 'validating…' });
    const r = await fetch(`/api/video/spec/${selected}`, {
      method: 'PUT', headers: { 'content-type': 'application/json' }, body: json,
    });
    const data = await r.json().catch(() => ({}));
    if (r.ok) { setStatus({ kind: 'ok', msg: `saved — ${data.scenes} scenes: ${(data.order || []).join(' ')}` }); setDirty(false); loadSpecs(); }
    else setStatus({ kind: 'err', msg: data.detail || data.error || 'save failed' });
  };

  const render = async () => {
    if (dirty) { setStatus({ kind: 'err', msg: 'save the spec before rendering' }); return; }
    const what = previewScene ? `scene “${previewScene}”` : (master ? 'full video' : 'draft');
    const time = previewScene ? 'seconds' : master ? 'several minutes' : 'a couple minutes';
    setStatus({ kind: 'busy', msg: `rendering ${what} · ${scale === 2 ? '4K' : '1080p'}${regenAudio ? ' + voiceover' : ''}… (${time})` });
    setVideoUrl(''); setLog('');
    try {
      // Render is async: POST kicks off a job (202), then we poll until done.
      const r = await fetch('/api/video/render', {
        method: 'POST', headers: { 'content-type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          name: selected, scale, regenAudio, master,
          previewScenes: previewScene ? [previewScene] : [],
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.jobId) { setStatus({ kind: 'err', msg: data.error || 'render failed' }); return; }
      // Poll the job. Renders run from ~10s (one scene) to several minutes (4K master).
      for (;;) {
        await new Promise((rs) => setTimeout(rs, 2500));
        const pr = await fetch(`/api/video/render/${data.jobId}`, { credentials: 'include' });
        const pd = await pr.json().catch(() => ({}));
        if (pd.status === 'done') { setVideoUrl(`${pd.url}?t=${Date.now()}`); setStatus({ kind: 'ok', msg: 'rendered ✓' }); setLog(pd.log || ''); break; }
        if (pd.status === 'error' || !pr.ok) { setStatus({ kind: 'err', msg: 'render failed' }); setLog(pd.log || ''); break; }
        // else still running → keep polling
      }
    } catch (e) { setStatus({ kind: 'err', msg: String((e as Error).message) }); }
  };

  const statusColor = status.kind === 'ok' ? c.good : status.kind === 'err' ? c.bad : c.mut;

  return (
    <div style={{ minHeight: '100vh', background: c.bg, color: c.ink, fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column' }}>
      <header style={{ padding: '14px 22px', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ width: 20, height: 20, borderRadius: 6, background: `linear-gradient(135deg,${c.accent},#81D674)` }} />
        <b style={{ fontSize: 16 }}>Studio · Video</b>
        <span style={{ color: c.mut, fontSize: 13 }}>the video is data · edit the spec, render, repeat</span>
        <span style={{ flex: 1 }} />
        <Link to="/assets" style={{ color: c.ink, fontSize: 13, textDecoration: 'none', border: `1px solid ${c.line}`, borderRadius: 8, padding: '4px 10px' }}>Assets</Link>
      </header>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* spec list */}
        <aside style={{ width: 260, borderRight: `1px solid ${c.line}`, padding: 12, overflowY: 'auto' }}>
          <button onClick={() => setShowNew((v) => !v)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: `1px dashed ${c.line}`, background: showNew ? c.panel : 'transparent', color: c.accent, fontWeight: 700, cursor: 'pointer', marginBottom: 12 }}>
            ＋ New from a brief
          </button>
          {showNew && (
            <div style={{ background: c.panel, border: `1px solid ${c.line}`, borderRadius: 10, padding: 11, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 11.5, color: c.mut, lineHeight: 1.4 }}>Describe the video. The AI author picks shots + writes the script, grounded in the Moxo brain.</div>
              <textarea placeholder="e.g. How a law firm onboards a new matter — intake, AI conflict check, partner sign-off"
                value={brief.topic} onChange={(e) => setBrief({ ...brief, topic: e.target.value })}
                style={{ minHeight: 60, resize: 'vertical', background: '#141418', color: c.ink, border: `1px solid ${c.line}`, borderRadius: 7, padding: 8, fontSize: 12.5, fontFamily: 'inherit' }} />
              <input placeholder="audience (optional)" value={brief.audience} onChange={(e) => setBrief({ ...brief, audience: e.target.value })}
                style={{ background: '#141418', color: c.ink, border: `1px solid ${c.line}`, borderRadius: 7, padding: '7px 8px', fontSize: 12.5 }} />
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={brief.type} onChange={(e) => setBrief({ ...brief, type: e.target.value })}
                  style={{ flex: 1, background: '#141418', color: c.ink, border: `1px solid ${c.line}`, borderRadius: 7, padding: '7px 6px', fontSize: 12.5 }}>
                  <option value="explainer">explainer</option>
                  <option value="feature">feature</option>
                  <option value="use-case">use-case</option>
                </select>
                <input type="number" min={20} max={150} value={brief.targetSec} onChange={(e) => setBrief({ ...brief, targetSec: Number(e.target.value) })}
                  title="target seconds" style={{ width: 58, background: '#141418', color: c.ink, border: `1px solid ${c.line}`, borderRadius: 7, padding: '7px 6px', fontSize: 12.5 }} />
              </div>
              <input placeholder="file name (optional)" value={brief.name} onChange={(e) => setBrief({ ...brief, name: e.target.value })}
                style={{ background: '#141418', color: c.ink, border: `1px solid ${c.line}`, borderRadius: 7, padding: '7px 8px', fontSize: 12.5 }} />
              <button onClick={author} disabled={authoring || !brief.topic.trim()}
                style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: c.accent, color: '#08252e', fontWeight: 700, cursor: authoring ? 'wait' : 'pointer' }}>
                {authoring ? 'authoring…' : 'Author spec'}
              </button>
            </div>
          )}
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.mut, marginBottom: 8 }}>Specs</div>
          {specs.map((s) => (
            <button key={s.name} onClick={() => selectSpec(s.name)}
              style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 6, padding: '9px 11px', borderRadius: 9,
                background: selected === s.name ? c.panel : 'transparent', border: `1px solid ${selected === s.name ? c.line : 'transparent'}`,
                color: c.ink, cursor: 'pointer' }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.title}</div>
              <div style={{ fontSize: 11.5, color: c.mut, marginTop: 2 }}>{s.type} · {s.scenes} scenes</div>
            </button>
          ))}
        </aside>

        {/* editor */}
        <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: 14, gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={save} disabled={!selected || status.kind === 'busy'}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: dirty ? c.accent : c.panel, color: dirty ? '#08252e' : c.ink, fontWeight: 700, cursor: 'pointer' }}>Save spec</button>
            <button onClick={render} disabled={!selected || status.kind === 'busy'}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#111', color: '#fff', fontWeight: 700, cursor: status.kind === 'busy' ? 'wait' : 'pointer' }}>▶ Render {previewScene ? 'scene' : master ? 'full' : 'draft'}</button>
            <select value={previewScene} onChange={(e) => setPreviewScene(e.target.value)}
              title="Render only one scene for the fastest preview"
              style={{ fontSize: 13, background: c.panel, color: c.ink, border: `1px solid ${c.line}`, borderRadius: 7, padding: '6px 8px' }}>
              <option value="">whole video</option>
              {sceneIds.map((id) => <option key={id} value={id}>scene: {id}</option>)}
            </select>
            <label style={{ fontSize: 13, color: c.mut, display: 'flex', alignItems: 'center', gap: 5 }} title="Full 10-bit HEVC + QA gate — slow, archival quality">
              <input type="checkbox" checked={master} onChange={(e) => setMaster(e.target.checked)} /> master
            </label>
            <label style={{ fontSize: 13, color: c.mut, display: 'flex', alignItems: 'center', gap: 5 }}>
              <input type="checkbox" checked={scale === 2} onChange={(e) => setScale(e.target.checked ? 2 : 1)} /> 4K
            </label>
            <label style={{ fontSize: 13, color: c.mut, display: 'flex', alignItems: 'center', gap: 5 }}>
              <input type="checkbox" checked={regenAudio} onChange={(e) => setRegenAudio(e.target.checked)} /> regen voiceover
            </label>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: statusColor }}>{status.msg}</span>
          </div>
          <textarea value={json} spellCheck={false}
            onChange={(e) => { setJson(e.target.value); setDirty(true); }}
            style={{ flex: 1, minHeight: 260, resize: 'none', background: '#141418', color: c.ink, border: `1px solid ${c.line}`, borderRadius: 10, padding: 14, fontFamily: c.mono, fontSize: 12.5, lineHeight: 1.5 }} />
          {log && <pre style={{ margin: 0, maxHeight: 120, overflow: 'auto', background: '#141418', border: `1px solid ${c.line}`, borderRadius: 8, padding: 10, fontSize: 11.5, color: c.mut, fontFamily: c.mono }}>{log}</pre>}
        </main>

        {/* preview */}
        <section style={{ width: 460, borderLeft: `1px solid ${c.line}`, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.mut }}>Preview</div>
          <div style={{ background: '#000', borderRadius: 10, overflow: 'hidden', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {videoUrl
              ? <video key={videoUrl} src={videoUrl} controls autoPlay style={{ width: '100%', height: '100%' }} />
              : <span style={{ color: c.mut, fontSize: 13 }}>{status.kind === 'busy' ? 'rendering…' : 'render to preview'}</span>}
          </div>
          <p style={{ fontSize: 12, color: c.mut, lineHeight: 1.5, margin: 0 }}>
            Edit the spec on the left (scenes, script, captions, timing) → Save (validates) → Render preview.
            Pick a single scene to preview in <b style={{ color: c.ink }}>seconds</b>; the default draft skips
            the archival encode. Tick <b style={{ color: c.ink }}>master</b> for the final 10-bit render.
          </p>
        </section>
      </div>
    </div>
  );
}
